// @flow

import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  InputLabel,
  Stack,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useHudVoucherApprovedMonths, {
  validateAvdOnApprovedVoucherMonth,
} from '../../../hooks/data-fetching/useHudVoucherApprovedMonths';
import type { Step1Props } from './types';
import generalAffordableMessages from '../../GeneralAffordable/messages';
import messages from './messages';
import {
  useHudFloorPlansWithPricing,
  useAnticipatedVoucherMonth,
} from './hooks';

// Precedence: no-permission (AVD-only, broader block) first, then the
// onboarded-certs lock, then no tooltip at all.
const resolveDateFieldDisabledTooltip = (
  onboardedTooltipMessage: MessageDescriptor,
  hasOnboardedCertifications: boolean,
  hasPermission: boolean = true,
): MessageDescriptor | null => {
  if (!hasPermission) {
    return generalAffordableMessages.editAnticipatedVoucherDateNoPermissionTooltip;
  }

  if (hasOnboardedCertifications) {
    return onboardedTooltipMessage;
  }

  return null;
};

// A disabled MUI input swallows pointer events, so the span wrapper is what
// makes the tooltip fire at all — shared across the date fields in this step.
const wrapWithDisabledTooltip = (
  element: React$Node,
  tooltipMessage: MessageDescriptor | null,
  intl: IntlShape,
): React$Node =>
  tooltipMessage ? (
    <Tooltip
      title={intl.formatMessage(tooltipMessage)}
      placement="top"
      variant="light"
    >
      <span style={{ display: 'block', width: '100%' }}>{element}</span>
    </Tooltip>
  ) : (
    element
  );

export const useStep1 = ({
  disabled,
  open,
  isEditMode,
  editingChange,
  reactHookFormProps,
}: Step1Props) => {
  const intl = useIntl();
  const { hudManageVouchers } = useFlags();
  const { setValue, watch, trigger, Controller, control } = reactHookFormProps;

  const [effectiveDate] = watch(['effectiveDate']);

  const today = useMemo(() => moment().startOf('day').toDate(), []);
  const priorValuesEffectiveDate = isEditMode ? effectiveDate : today;
  const { floorPlans, isLoading, isError } = useHudFloorPlansWithPricing(
    open,
    priorValuesEffectiveDate,
  );
  const hasEditAnticipatedVoucherDatePermission = usePermissionsHook({
    scope: ['edit-anticipated-voucher-date'],
  });
  const hasOnboardedCertifications = Boolean(
    editingChange?.hasOnboardedCertifications,
  );

  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const { blockedYearMonths, hasVouchersSetUp } = useHudVoucherApprovedMonths({
    organizationId,
    propertyId,
    // The AVD picker's own floor (shouldDisableMonth below) and this query
    // window both key off effectiveDate, so they always agree on which
    // months are in play.
    fromYearMonth: effectiveDate,
    enabled: hudManageVouchers,
  });
  // Approved/Paid voucher-month blocking now applies to both flows, gated
  // only by hasVouchersSetUp (itself behind the hudManageVouchers flag).
  const avdTooltipMessage = hasVouchersSetUp
    ? generalAffordableMessages.avdVoucherAwareTooltip
    : generalAffordableMessages.anticipatedVoucherDateTooltip;

  const effectiveDateKey = effectiveDate
    ? formatDateDB(effectiveDate, false) || null
    : null;

  const [avdOverrideForDate, setAvdOverrideForDate] = useState(null);
  const isAvdOverridden =
    avdOverrideForDate !== null && avdOverrideForDate === effectiveDateKey;

  // Defense-in-depth: both fields are disabled below when onboarded, and in
  // edit mode the saved AVD must survive open — only an actual Effective
  // Date change (not just re-opening on the saved record) should recompute.
  const shouldAutoComputeAvd =
    !isAvdOverridden &&
    !hasOnboardedCertifications &&
    (!isEditMode ||
      !effectiveDate ||
      formatDateDB(effectiveDate) !== editingChange?.effectiveDate);

  const {
    anticipatedVoucherMonth: computedAnticipatedVoucherMonth,
    isLoading: isAnticipatedVoucherMonthLoading,
  } = useAnticipatedVoucherMonth(effectiveDate, open && shouldAutoComputeAvd);

  useEffect(() => {
    if (!shouldAutoComputeAvd || isAnticipatedVoucherMonthLoading) {
      return;
    }

    setValue('anticipatedVoucherMonth', computedAnticipatedVoucherMonth);
    setValue(
      'autoCalculatedAnticipatedVoucherMonth',
      computedAnticipatedVoucherMonth,
    );
  }, [
    shouldAutoComputeAvd,
    isAnticipatedVoucherMonthLoading,
    computedAnticipatedVoucherMonth,
    setValue,
  ]);

  const step1HeaderFields = useMemo(() => {
    const fields = [
      {
        variant: 'custom',
        name: 'effectiveDate',
        GridProps: { xs: 12, md: 6 },
        component: (
          <Controller
            name="effectiveDate"
            control={control}
            rules={{ required: true }}
            render={({ field: rhfField, fieldState }) => {
              const isEffectiveDateDisabled =
                disabled || hasOnboardedCertifications;
              const disabledTooltipMessage = resolveDateFieldDisabledTooltip(
                messages.onboardedCertificationsEffectiveDateTooltip,
                hasOnboardedCertifications,
              );

              return wrapWithDisabledTooltip(
                <DatePicker
                  {...rhfField}
                  onChange={(value) => {
                    rhfField.onChange(value);
                    trigger('effectiveDate');
                  }}
                  label={intl.formatMessage(messages.effectiveDate)}
                  disabled={isEffectiveDateDisabled}
                  readOnly={isEffectiveDateDisabled}
                  error={Boolean(fieldState.error)}
                  helperText={intl.formatMessage(messages.required)}
                  id="effectiveDate_input"
                />,
                disabledTooltipMessage,
                intl,
              );
            }}
          />
        ),
      },
      {
        variant: 'custom',
        name: 'anticipatedVoucherMonth',
        GridProps: { xs: 12, md: 6 },
        component: (
          <Controller
            name="anticipatedVoucherMonth"
            control={control}
            rules={{
              required: true,
              validate: (value) =>
                validateAvdOnApprovedVoucherMonth(
                  value,
                  blockedYearMonths,
                  intl,
                ),
            }}
            render={({ field: rhfField, fieldState }) => {
              const isAvdDisabled =
                disabled ||
                !effectiveDate ||
                !hasEditAnticipatedVoucherDatePermission ||
                hasOnboardedCertifications;

              const disabledTooltipMessage = resolveDateFieldDisabledTooltip(
                messages.onboardedCertificationsAvdTooltip,
                hasOnboardedCertifications,
                hasEditAnticipatedVoucherDatePermission,
              );
              const datePicker = (
                <DatePicker
                  {...rhfField}
                  onChange={(value) => {
                    rhfField.onChange(value);
                    trigger('anticipatedVoucherMonth');
                    setAvdOverrideForDate(effectiveDateKey);
                  }}
                  openTo="month"
                  views={['year', 'month']}
                  disabled={isAvdDisabled}
                  readOnly={isAvdDisabled}
                  shouldDisableMonth={
                    effectiveDate
                      ? (date) =>
                          moment(date).isBefore(moment(effectiveDate), 'month')
                      : undefined
                  }
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  id="anticipatedVoucherMonth_input"
                />
              );

              return (
                <Stack spacing={0.5}>
                  <InputLabel
                    htmlFor="anticipatedVoucherMonth_input"
                    id="anticipatedVoucherMonth_label"
                    disabled={isAvdDisabled}
                    TooltipProps={{
                      title: intl.formatMessage(avdTooltipMessage),
                      children: <AlertInfoIcon fontSize="small" />,
                    }}
                  >
                    {intl.formatMessage(messages.anticipatedVoucherMonth)}
                  </InputLabel>
                  {wrapWithDisabledTooltip(
                    datePicker,
                    disabledTooltipMessage,
                    intl,
                  )}
                </Stack>
              );
            }}
          />
        ),
      },
    ];

    return fields;
  }, [
    avdTooltipMessage,
    blockedYearMonths,
    control,
    disabled,
    effectiveDate,
    effectiveDateKey,
    hasEditAnticipatedVoucherDatePermission,
    hasOnboardedCertifications,
    intl,
    trigger,
  ]);

  return {
    floorPlans,
    isLoading,
    isError,
    step1HeaderFields,
  };
};
